import { Document, Page, Text, View, StyleSheet, Font } from "@react-pdf/renderer";
import { TreatmentPrescription } from "../treatment-prescription/types";
import {
	displayEstimatedSessions,
	activeServicesProvidedFilter,
	plannedServicesProvidedFilter,
	finishedServicesProvidedFilter,
} from "../treatment-prescription/utils";
import { ClinicInfo } from "../../../utils/makeClinicInfo";
import { NFB_SERVICE } from "../../../utils/constants";
import { TechnicianInfo } from "../../../utils/makeTechnicianInfo";
import { PdfHeader } from "../../../components/pdf/document-components/PdfHeader";
import { PdfFooter } from "../../../components/pdf/document-components/PdfFooter";
import { PdfTechnicianSignature } from "../../../components/pdf/document-components/PdfTechnicianSignature";

Font.register({
	family: "Montserrat",
	fonts: [
		{ src: "/fonts/Montserrat/static/Montserrat-Regular.ttf", fontWeight: "normal" },
		{ src: "/fonts/Montserrat/static/Montserrat-Bold.ttf", fontWeight: "bold" },
	],
});

type TreatmentPrescriptionDocumentProps = {
	treatmentPrescription: TreatmentPrescription;
	clinics: Array<ClinicInfo>;
	prescriberTechnician: TechnicianInfo;
};

export const TreatmentPrescriptionDocument = ({
	treatmentPrescription,
	clinics,
	prescriberTechnician,
}: TreatmentPrescriptionDocumentProps) => {
	const client = treatmentPrescription?.treatment?.client;
	const clientName = client?.user_id?.first_name.trim() + " " + client?.user_id?.last_name;
	const servicesPrescribedArranged = [...(treatmentPrescription?.services_prescribed || [])].sort((a, b) => {
		if (a.service.id === NFB_SERVICE) return -1;
		if (b.service.id === NFB_SERVICE) return 1;
		return a.service.id - b.service.id;
	});

	const servicesProvided = treatmentPrescription?.services_provided;

	const servicesProvidedByService = servicesProvided?.reduce(
		(acc: Record<number, { periodicity: any; sessionQuantity: any }>, service) => {
			const updatedAcc = { ...acc };
			const serviceId = service.service.id;

			if (!updatedAcc[serviceId]) {
				const servicesForThisService = servicesProvided.filter((sp) => sp.service.id === serviceId);
				const activeService = servicesForThisService.find(activeServicesProvidedFilter);
				const plannedService = servicesForThisService.find(plannedServicesProvidedFilter);
				const finishedService = servicesForThisService.find(finishedServicesProvidedFilter);

				const selectedService = activeService || plannedService || finishedService;

				if (selectedService) {
					updatedAcc[serviceId] = {
						periodicity: selectedService.session_periodicity,
						sessionQuantity: selectedService.session_quantity,
					};
				}
			}

			return updatedAcc;
		},
		{}
	);

	return (
		<Document>
			<Page size="A4" style={styles.page}>
				<PdfHeader />
				<View style={styles.contentWrapper}>
					<View style={styles.titleContainer}>
						<Text style={styles.title}>PRESCRIÇÃO DE TRATAMENTO</Text>
					</View>

					{/* Client Information */}
					<View style={styles.sectionContainer}>
						<Text style={styles.sectionTitle}>Nome</Text>
						<View style={styles.infoBox}>
							<Text style={styles.text}>{clientName}</Text>
						</View>
					</View>

					{/* Services Prescribed Section */}
					<View style={styles.sectionContainer}>
						<Text style={styles.sectionTitle}>Serviços Prescritos</Text>
						<View style={styles.table}>
							<View style={styles.tableHeader}>
								<Text style={[styles.tableCellHeader, styles.col60]}>Serviço</Text>
								<Text style={[styles.tableCellHeader, styles.col40]}>Periodicidade</Text>
								<Text style={[styles.tableCellHeader, styles.col40]}>Sessões Estimadas</Text>
							</View>
							{servicesPrescribedArranged?.map((servicePrescribed, index) => {
								const backgroundColor = index % 2 === 0 ? "transparent" : "#f5f2f5";
								return (
									<View key={servicePrescribed.id} style={[styles.tableRow, { backgroundColor }]}>
										<Text style={[styles.tableCell, styles.col60]}>
											{servicePrescribed.service.name}
										</Text>
										<Text style={[styles.tableCell, styles.col40]}>
											{getPeriodicity(servicePrescribed.service.id, servicesProvidedByService)}
										</Text>
										<Text style={[styles.tableCell, styles.col40]}>
											{displayEstimatedSessions(
												servicePrescribed.estimated_total_sessions,
												servicePrescribed.service.id
											)}
										</Text>
									</View>
								);
							})}
						</View>
					</View>
				{servicesPrescribedArranged?.some(
					(servicePrescribed) => servicePrescribed.goals && servicePrescribed.goals.length > 0
				) && (
					<View style={styles.sectionContainer} wrap>
						<Text style={styles.sectionTitle} minPresenceAhead={30}>Objetivos Terapêuticos</Text>
						<View style={styles.infoBox} wrap>
							{servicesPrescribedArranged?.map((servicePrescribed) => {
									const serviceGoals = servicePrescribed.goals;
									const hasGoals = serviceGoals && serviceGoals.length > 0;

									if (!hasGoals) return null;

									return (
										<View key={servicePrescribed.id} style={{ marginBottom: 6 }} wrap minPresenceAhead={20}>
											<Text style={styles.subSectionTitle}>
												{servicePrescribed.service.name}:
											</Text>
											<View style={styles.infoBox} wrap>
												{serviceGoals.map((goal) => (
													<Text key={goal.id} style={[styles.text, { marginTop: 1 }]}>
														• {goal.treatment_therapy_goals_id.goal || ""}
													</Text>
												))}
											</View>
										</View>
									);
								})}
							</View>
						</View>
					)}
					<PdfTechnicianSignature technician={prescriberTechnician} breakPage={false} variation="compact" />
				</View>
				<PdfFooter clinics={clinics} />
			</Page>
		</Document>
	);
};

const getPeriodicity = (
	serviceId: number,
	servicesProvidedByService: Record<number, { periodicity: any; sessionQuantity: any }>
) => {
	const service = servicesProvidedByService[serviceId];
	if (!service) return "Não Aplicável";
	const periodicity =
		service?.periodicity === "weekly" ? "Semana" : service?.periodicity === "monthly" ? "Mês" : "Não Aplicável";

	const sessionsPerPeriodicity =
		service?.sessionQuantity !== 0 ? `${service?.sessionQuantity} / ${periodicity}` : periodicity;
	return sessionsPerPeriodicity;
};
const logoColor = "#013567"
const styles = StyleSheet.create({
	page: {
		padding: 50,
		paddingTop: 40,
		paddingBottom: 90,
		fontFamily: "Montserrat",
		fontSize: 9,
	},
	titleContainer: {
		alignItems: "center",
		marginBottom: 22,
	},
	title: {
		fontSize: 16,
		fontWeight: "bold",
		color: "#2c3e50",
		letterSpacing: 0.5,
	},
	contentWrapper: {
		flexGrow: 1,
		flexShrink: 1,
	},
	rowContainer: {
		flexDirection: "row",
		justifyContent: "space-between",
		gap: 15,
	},
	sectionContainer: {
		marginBottom: 22,
	},
	halfSection: {
		width: "48%",
		flexShrink: 1,
	},

	sectionTitle: {
		fontSize: 11,
		fontWeight: "bold",
		color: logoColor,
		textTransform: "uppercase",
		letterSpacing: 0.8,
		marginBottom: 10,
	},
	subSectionTitle: {
		fontSize: 10,
		fontWeight: "bold",
		color: "#2c3e50",
		letterSpacing: 0.3,
		marginBottom: 3,
	},
	infoBox: {
		paddingLeft: 10,
	},
	text: {
		fontSize: 9,
		color: "#2c3e50",
		lineHeight: 1.6,
	},
	table: {
		width: "100%",
		borderWidth: 1.5,
		borderColor: logoColor,
		borderRadius: 2,
		overflow: "hidden",
	},
	tableHeader: {
		flexDirection: "row",
		backgroundColor: logoColor,
		paddingVertical: 2,
	},
	tableRow: {
		flexDirection: "row",
		borderBottomWidth: 0.5,
		borderBottomColor: "#e2e8f0",
	},
	tableCell: {
		padding: 6,
		fontSize: 8.5,
		color: "#2c3e50",
		wordWrap: "break-word",
	},
	tableCellHeader: {
		padding: 6,
		fontSize: 9,
		fontWeight: "bold",
		color: "#ffffff",
		wordWrap: "break-word",
	},
	col60: {
		width: "60%",
	},
	col40: {
		width: "40%",
		textAlign: "center",
	},

});
